import PropTypes from "prop-types";
import * as React from "react";
import { SignType } from "../../../domain";
import { useCurrentUser } from "../../../globalState";
import { signedTypeOptionsFor } from "../../../domain/productComposer/signedTypeOptions";
import { bulkProductAction } from "../../../services/bulkProduct";
import { Alert, Button, Checkbox, Dialog, Select } from "../../../themeV2";
import css from "./BulkDuplicateDialog.module.css";

const signedTypes = SignType.list();

const BulkDuplicateDialog = React.memo(function BulkDuplicateDialog({
  open,
  onClose,
  selectedIds,
  onActionComplete,
}) {
  const currentUser = useCurrentUser();
  const signedTypeOptions = signedTypeOptionsFor({
    signedTypes,
    user: currentUser,
  });
  const [signedType, setSignedType] = React.useState("");
  const [deactivateParent, setDeactivateParent] = React.useState(false);
  const [isLoading, setIsLoading] = React.useState(false);
  const [error, setError] = React.useState(null);

  const handleClose = React.useCallback(() => {
    setSignedType("");
    setDeactivateParent(false);
    setError(null);
    onClose();
  }, [onClose]);

  const handleOpenChange = React.useCallback(
    (nextOpen) => {
      if (nextOpen || isLoading) return;
      handleClose();
    },
    [isLoading, handleClose],
  );

  const handleDuplicate = React.useCallback(async () => {
    if (!signedType) return;

    setIsLoading(true);
    setError(null);

    try {
      await bulkProductAction({
        productIds: [...selectedIds],
        action: "duplicate",
        signedType,
        deactivateParent,
      });
      setSignedType("");
      setDeactivateParent(false);
      onActionComplete();
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  }, [signedType, deactivateParent, selectedIds, onActionComplete]);

  const plural = selectedIds.size !== 1 ? "s" : "";

  return (
    <Dialog.Root open={open} onOpenChange={handleOpenChange}>
      <Dialog.Popup size="md">
        <Dialog.Title>
          Duplicate {selectedIds.size} Product{plural}
        </Dialog.Title>

        <Select.Root
          label="Signed Type"
          placeholder="Select a signed type"
          value={signedType}
          onValueChange={setSignedType}
        >
          {signedTypeOptions.map((type) => (
            <Select.Item key={type} value={type}>
              {SignType.toLabel(type)}
            </Select.Item>
          ))}
        </Select.Root>

        <div className={css.deactivate}>
          <Checkbox
            checked={deactivateParent}
            onCheckedChange={(checked) => setDeactivateParent(checked)}
            label={`Deactivate selected product${plural} after duplicating`}
          />
        </div>

        {error && (
          <div className={css.error}>
            <Alert severity="error">{error}</Alert>
          </div>
        )}

        <Dialog.Actions>
          <Button variant="text" disabled={isLoading} onClick={handleClose}>
            Cancel
          </Button>
          <Button
            variant="filled"
            disabled={!signedType}
            loading={isLoading}
            onClick={handleDuplicate}
          >
            Duplicate
          </Button>
        </Dialog.Actions>
      </Dialog.Popup>
    </Dialog.Root>
  );
});

BulkDuplicateDialog.displayName = "BulkDuplicateDialog";

BulkDuplicateDialog.propTypes = {
  onActionComplete: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired,
  selectedIds: PropTypes.instanceOf(Set).isRequired,
};

export default BulkDuplicateDialog;
